import {useState, useEffect, useContext, forwardRef} from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Slide,
  Grid,
  TextField,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormGroup,
  FormControlLabel,
  Switch,
  Box,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { EventContext } from "../../../contexts/EventContext";
// import ProfileTextField from "./components/ProfileTextField";
// import ProfileSelectField from "./components/ProfileSelectField";

import request from "../../../utilities/requests";

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const EditAdminUserDialog = ({ open, close, user }) => {
  const { userData } = useContext(EventContext)
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [error, setError] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [canViewPermissions, setCanViewPermissions] = useState(false);
  const [permissions, setPermissions] = useState(null);
  const [groups, setGroups] = useState([]);

  useEffect(() => {
    if (user) {
      setUsername(user.username);
      setEmail(user.email);
      setPhone(user.phone);
      setFirstName(user?.profile?.firstName);
      setLastName(user?.profile?.lastName);
      if (userData?.role === 'superadmin' || userData?.permissions?.canManageAdminUsers) {
        setCanViewPermissions(true);
        getPermissions();
      }
    }
  }, [user]);

  const getPermissions = async () => {
    const result = await request(`users/permissions?userId=${user._id}`);
    if (result.success) {
      setPermissions(result.permissions);
      setGroups(result.groups)
    }
  };

  const convertLabel = (field) => {
    const result = field.replace(/([A-Z])/g, " $1");
    const finalResult = result.charAt(0).toUpperCase() + result.slice(1);
    return finalResult;
  };

  const renderPermissions = () => {
    if (!permissions) {
      return <></>;
    }

    // 1) Global toggle state for all permissions (excluding timeOnly)
    const permissionKeys = Object.keys(permissions);
    const nonTimeOnlyKeys = permissionKeys.filter((key) => key !== "timeOnly");
    const allEnabled = nonTimeOnlyKeys.every((key) => permissions[key] === true);
    const anyEnabled = nonTimeOnlyKeys.some((key) => permissions[key] === true);

    const renderedPermissions = new Set();

    const renderGroup = (title, permissionKeys) => {
      const validPermissions = permissionKeys.filter(
        (key) => permissions.hasOwnProperty(key)
      );

      if (!validPermissions.length) {
        return null;
      }

      const allEnabled = validPermissions.every((key) => permissions[key] === true);
      const anyEnabled = validPermissions.some((key) => permissions[key] === true);

      return (
        <Accordion key={title}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Grid
              container
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="button">
                {title.charAt(0).toUpperCase() + title.slice(1)}
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={allEnabled}
                    indeterminate={!allEnabled && anyEnabled}
                    onChange={() => {
                      const nextValue = !allEnabled;
                      const updates = {};
                      validPermissions.forEach((p) => {
                        updates[p] = nextValue;
                      });
                      // Enabling permissions in a group turns off timeOnly
                      if (nextValue === true) updates.timeOnly = false;
                      updateMultiplePermissions(updates);
                    }}
                  />
                }
                label={allEnabled ? "Disable all" : "Enable all"}
                onClick={(e) => e.stopPropagation()}
              />
            </Grid>
          </AccordionSummary>
          <AccordionDetails>
            <FormGroup>
              {validPermissions.map((field) => {
                renderedPermissions.add(field);
                return (
                  <FormControlLabel
                    key={field}
                    control={
                      <Switch
                        checked={permissions[field]}
                        onChange={() => handleSwitch(field)}
                      />
                    }
                    label={convertLabel(field)}
                  />
                );
              })}
            </FormGroup>
          </AccordionDetails>
        </Accordion>
      );
    };

    const groupedAccordions = groups.map((group) =>
      renderGroup(group.name, group.permissions || [])
    );

    const otherPermissions = Object.keys(permissions).filter(
      (key) => !renderedPermissions.has(key) && key !== "timeOnly"
    );

    const otherAccordion = renderGroup("Other", otherPermissions);

    return (
      <Grid item>
        {/* Time Only — standalone section, only visible when work log is enabled for this org */}
        {"timeOnly" in permissions && userData?.org?.config?.workLogEnabled && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box>
                <Typography variant="button">Time Only</Typography>
                <Typography variant="body2" color="text.secondary">
                  Restricts this user to the work log entry screen only. Overrides all other permissions.
                </Typography>
              </Box>
              <Switch
                checked={!!permissions.timeOnly}
                onChange={() => handleSwitch("timeOnly")}
              />
            </Box>
          </Paper>
        )}

        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="button">Permissions</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {/* Global permissions toggle */}
            <FormControlLabel
              sx={{ mb: 2 }}
              control={
                <Switch
                  checked={allEnabled}
                  indeterminate={!allEnabled && anyEnabled}
                  onChange={() => {
                    const nextValue = !allEnabled;
                    const updates = {};
                    permissionKeys.forEach((p) => {
                      // Enable all: timeOnly = false; Disable all: timeOnly = true
                      updates[p] = p === "timeOnly" ? !nextValue : nextValue;
                    });
                    updateMultiplePermissions(updates);
                  }}
                />
              }
              label={allEnabled ? "Disable all permissions" : "Enable all permissions"}
            />
            {groupedAccordions}
            {otherAccordion}
          </AccordionDetails>
        </Accordion>
      </Grid>
    );
  };

  const handleSwitch = async (field) => {
    const newValue = !permissions[field];
    const updates = { [field]: newValue };

    if (field === "timeOnly" && newValue === true) {
      // Turning on timeOnly — clear all other permissions
      Object.keys(permissions).forEach((key) => {
        if (key !== "timeOnly") updates[key] = false;
      });
    } else if (field !== "timeOnly" && newValue === true) {
      // Turning on any other permission — turn off timeOnly
      updates.timeOnly = false;
    }

    updateMultiplePermissions(updates);
  };

  const updateMultiplePermissions = async (updates) => {
    const previous = { ...permissions };

    // Optimistically update UI
    Object.keys(updates).forEach((key) => {
      permissions[key] = updates[key];
    });
    setPermissions({ ...permissions });

    const body = {
      userId: user._id,
      updates,
    };

    const result = await request("users/permissions", "post", body);

    if (!result.success) {
      // rollback
      setPermissions(previous);
    }
  };

  const handleSave = async () => {
    let error;
    if (!username) {
      error = "Please enter a username";
    } else if (!email) {
      error = "Please enter an email";
    } else if (!phone) {
      error = "Please enter a phone number";
    } else if (!firstName) {
      error = "Please enter a first name";
    } else if (!lastName) {
      error = "Please enter a last name";
    }
    if (error) {
      setError(error);
      return;
    } else {
      setError(null);
    }

    const body = {
      userId: user._id,
      phone,
      firstName,
      lastName,
    };

    const result = await request("profile/adminupdate", "post", body);
    if (!result.success) {
      setError(result.error);
      return;
    }
    close();
  };

  const handleDelete = async () => {
    const body = {
      userId: user._id,
      admin: true,
    };

    const result = await request("users/delete", "post", body);
    if (!result.success) {
      return;
    }
    setShowConfirm(false);
    close();
  };

  return (
    <div>
      <Dialog
        fullScreen
        open={open}
        onClose={close}
        TransitionComponent={Transition}
      >
        <AppBar sx={{ position: "relative" }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={close}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              Edit Profile
            </Typography>
            <Button autoFocus color="inherit" onClick={handleSave}>
              save
            </Button>
          </Toolbar>
        </AppBar>
        <Grid container direction="column" spacing={3} style={{ padding: 20 }}>
          {error ? (
            <Grid item>
              <Alert severity="error">{error}</Alert>
            </Grid>
          ) : (
            <></>
          )}

          <Grid item>
            <TextField fullWidth label="username" value={username} disabled />
          </Grid>
          <Grid item>
            <TextField fullWidth label="email" value={email} disabled />
          </Grid>
          <Grid item>
            <TextField
              fullWidth
              label="phone*"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </Grid>
          <Grid item>
            <TextField
              fullWidth
              label="first name*"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </Grid>
          <Grid item>
            <TextField
              fullWidth
              label="last name*"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </Grid>
          {canViewPermissions ? renderPermissions() : <></>}

          <Grid item>
            <Button
              variant="outlined"
              onClick={handleSave}
              style={{ width: "100%" }}
            >
              Save
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              onClick={() => setShowConfirm(true)}
              style={{ width: "100%" }}
              color="secondary"
            >
              Delete
            </Button>
          </Grid>
        </Grid>
      </Dialog>
      <Dialog open={showConfirm} onClose={() => setShowConfirm(false)}>
        <DialogTitle id="alert-dialog-title">{"Confirm Delete"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete {user?.profile?.firstName}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirm(false)}>Cancel</Button>
          <Button onClick={handleDelete} autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default EditAdminUserDialog;
